import styled from 'styled-components';
import React, {FC} from 'react';
import {Link} from 'gatsby';

import {
  mediaBreakpointDown,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  transition
} from '../../../../lib/styleVariables';
import {headerHeight} from './headerVariables';
import {SecondaryButton} from '../../../molecules/buttonsR4';
import {hiddenMobileDownStyle, hiddenTabletDownStyle} from '../../../../lib/responsiveClassNames';
import {paletteR4} from '../../../../lib/styleVariablesR4';
import frontLogo from '../../../../assets/images/frontPageLogo.svg';
import {SocialMediaLink, SocialMediaLinks} from '../../socialMedia/widget';
import {Handler} from '../../../../lib/typeHelpers';
import {Icon} from '../../../molecules/icon';
import {useTranslation} from '../../../../context/translation';
import {useWindowScroll} from '../../../../lib/hooks/useWindowScroll';
import {useIsServer} from '../../../../lib/hooks/useIsServer';
import {convertHexToRGBA} from '../../../../lib/color-utils';

const MAX_SCROLL_DISTANCE = 58; // One line topbar height

interface MenuProps {
  isSearchPage: boolean;
  isHighlighted: boolean;
  hasSubscriptionForm: boolean;
  socialMediaLinks: SocialMediaLink[];
  isMenuOpen: boolean;
  isSearchOpen: boolean;
  toggleMenuOverlay: Handler;
  toggleSearchOverlay: Handler;
  openSubscriptionOverlay: Handler;
}

type WrapperTypes = {
  backgroundColor: string;
  boxShadow: string;
};

const StyledWrapper = styled.div<WrapperTypes>`
  background: ${props => props.backgroundColor};
  box-shadow: ${props => props.boxShadow};
  transition: ${transition.fast};
`;

const StyledWrapperInner = styled.div`
  max-width: 1210px;
  padding: 0 20px;
  margin: 0 auto;
  height: ${headerHeight.desktop + 1}px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  z-index: 3;

  ${mediaBreakpointOnly('tablet')} {
    height: ${headerHeight.tablet + 1}px;
  }

  ${mediaBreakpointDown('mobile')} {
    height: ${headerHeight.mobile + 1}px;
  }
`;

const StyledHeaderLeft = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
`;

const StyledLogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  z-index: 1;
`;

const StyledLogo = styled.img`
  height: 34px;

  ${mediaBreakpointDown('tablet')} {
    height: 24px;
  }
`;

const StyledButton = styled(SecondaryButton)<{isVisible: boolean}>`
  margin-left: 36px;
  transition: ${transition.fast};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  ${hiddenTabletDownStyle};
`;

const StyledSocialMediaLinksWrapper = styled.div<{isVisible: boolean}>`
  display: flex;
  color: ${paletteR4.black.base};
  transition: ${transition.fast};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  ${hiddenMobileDownStyle};
`;

const socialMediaLinkStyles = `
  padding: 10px;
  transition: ${transition.fast};
  
  &:not(:last-child) {
    margin-right: 10px;
  }

  &:hover {
    color: ${paletteR4.gray.base};
  }
`;

const HeaderRightIcon = styled.button`
  width: 40px;
  height: 40px;
  padding: 10px;
  background: transparent;
  border: none;
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-left: 40px;
  }

  ${mediaBreakpointDown('tablet')} {
    margin-left: 10px;
  }

  svg {
    * {
      fill: none;
    }
  }
`;

export const Menu: FC<MenuProps> = props => {
  const {
    isSearchPage,
    isHighlighted,
    hasSubscriptionForm,
    socialMediaLinks,
    isMenuOpen,
    isSearchOpen,
    toggleSearchOverlay,
    toggleMenuOverlay,
    openSubscriptionOverlay
  } = props;

  const isOverlayOpen = isMenuOpen || isSearchOpen;
  const isServer = useIsServer();
  const {t} = useTranslation();
  const {scrollY} = useWindowScroll();
  const isScrolled = scrollY > MAX_SCROLL_DISTANCE;

  const defaultBackgroundColor = paletteR4.white.base;
  const defaultBoxShadow = `0px 4px 16px ${convertHexToRGBA(paletteR4.black.base, 0.08)}`;

  const getInitialHeaderBackgroundColor = () => {
    if (isSearchPage)
      return paletteR4.yellow.highlight;
    if (!isHighlighted)
      return 'transparent';

    return defaultBackgroundColor;
  };

  const getHeaderBackgroundColor = () => {
    if (isSearchOpen)
      return paletteR4.yellow.highlight;
    if (isServer || !(isScrolled || isMenuOpen))
      return getInitialHeaderBackgroundColor();

    return defaultBackgroundColor;
  };

  const getHeaderBoxShadow = () => {
    if (isServer || !isScrolled)
      return 'none';

    return defaultBoxShadow;
  };

  return (
    <StyledWrapper
      backgroundColor={getHeaderBackgroundColor() || defaultBackgroundColor}
      boxShadow={getHeaderBoxShadow()}
    >
      <StyledWrapperInner>
        <StyledHeaderLeft>
          <StyledLogoContainer to="/blog">
            <StyledLogo src={frontLogo} />
          </StyledLogoContainer>
          {hasSubscriptionForm && (
            <StyledButton
              transparentBackground
              isVisible={isScrolled}
              onClick={() => openSubscriptionOverlay()}
            >
              {t('front-page.home.subscribe-button-label')}
            </StyledButton>
          )}
        </StyledHeaderLeft>
        <StyledSocialMediaLinksWrapper isVisible={!isOverlayOpen}>
          <SocialMediaLinks
            links={socialMediaLinks}
            size={20}
            $css={socialMediaLinkStyles}
            designVersion="r4"
          />
        </StyledSocialMediaLinksWrapper>
        <HeaderRightIcon onClick={() => toggleSearchOverlay()}>
          <Icon
            color={isOverlayOpen ? paletteR4.red.lowlight : paletteR4.black.base}
            name={isSearchOpen ? 'exitFrontPage' : 'magnifyingGlassFrontPage'}
            size={16}
            useR4
          />
        </HeaderRightIcon>
        <HeaderRightIcon onClick={() => toggleMenuOverlay()}>
          <Icon
            color={isOverlayOpen ? paletteR4.red.lowlight : paletteR4.black.base}
            name={isMenuOpen ? 'exitFrontPage' : 'hamburgerMenuFrontPage'}
            size={16}
            useR4
          />
        </HeaderRightIcon>
      </StyledWrapperInner>
    </StyledWrapper>
  );
};
